import { Button } from '@storeblocks/button';
import { type FC, useState } from 'react';
import styled from 'styled-components';

import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { useCurrentUserInfo } from '@/auth/hooks';
import { UserType } from '@/config/types';
import { isInternalUser, isLocalhost } from '@/config/utils';
import { setUserTypeToCookie } from '@/config/utils/setUserTypeToCookie';
import { shouldFetchConfigFromBackend } from '@/config/utils/shouldFetchConfigFromBackend';
import { useCopyBearerToken } from '@/pages/Debug/useCopyBearerToken';
import { isBetaFeaturesOn } from '@/util/isBetaFeaturesOn';
import { removeBetaFeaturesCookie } from '@/util/removeBetaFeaturesCookie';
import { setBetaFeaturesCookieOn } from '@/util/setBetaFeaturesCookieOn';

import { InternalLink } from '../InternalLink';
import { WithGap } from '../WithGap';
import { RoleManager } from './RoleManager';
import { ViewTokenData } from './ViewTokenData';

export const DevelopmentTools: FC = () => {
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(true);
  const { copyToClipboard, loading, success } = useCopyBearerToken();

  const organizationCmId = useOrganizationCmId();
  const userInfo = useCurrentUserInfo();

  const toggleDevMode = (): void => {
    if (isBetaFeaturesOn()) {
      removeBetaFeaturesCookie();
    } else {
      setBetaFeaturesCookieOn();
    }

    document.location.reload();
  };

  const toggleUserType = (): void => {
    setUserTypeToCookie(
      isInternalUser() ? UserType.External : UserType.Internal,
    );

    document.location.reload();
  };

  if (!show) {
    return null;
  }

  return (
    <>
      <Wrapper data-test="developer-tools">
        <WithGap gap="8">
          <Header>
            <h3>Developer Tools</h3>

            <Button
              variant="tertiary"
              iconLeft="close"
              onClick={() => setShow(false)}
            />
          </Header>

          <SignedInUser>
            <div>Signed in as</div>
            <div>{userInfo.name}</div>
            {userInfo.userCmId && <div>CMID {userInfo.userCmId}</div>}
          </SignedInUser>

          <Button
            onClick={() => copyToClipboard()}
            variant="subtle"
            iconLeft="copy"
            title="Copy the bearer authentication token to your clipboard."
            loading={loading}
            disabled={loading}
          >
            {success ? 'Copied!' : 'Copy token to clipboard'}
          </Button>

          <ViewTokenData />

          <Button onClick={toggleDevMode} variant="subtle">
            Show beta content: {isBetaFeaturesOn() ? 'ON' : 'OFF'}
          </Button>

          {isLocalhost() && (
            <Button
              // Cannot toggle user type when config is fetched from backend.
              disabled={shouldFetchConfigFromBackend()}
              title={
                shouldFetchConfigFromBackend()
                  ? 'Cannot toggle user type when config is fetched from backend.'
                  : undefined
              }
              onClick={toggleUserType}
              variant="subtle"
            >
              {`User type: ${isInternalUser() ? 'INTERNAL' : 'EXTERNAL'}`}
            </Button>
          )}

          {organizationCmId && (
            <>
              <Button
                onClick={() => setShowRoleModal(true)}
                variant="subtle"
                data-test="role-manager-button"
              >
                Role manager
              </Button>
              <Button
                as={InternalLink}
                to={`${organizationCmId}/debug`}
                iconRight="arrow_right"
                variant="tertiary"
              >
                Debug page
              </Button>
            </>
          )}
        </WithGap>
      </Wrapper>

      {organizationCmId && (
        /* The role manager modal. */
        <RoleManager
          show={showRoleModal}
          onClose={() => setShowRoleModal(false)}
        />
      )}
    </>
  );
};

const Wrapper = styled.div`
  position: fixed;
  z-index: 1000;
  bottom: 0;
  right: 0;
  padding: 20px;
  background: var(--p-color-surface-1);
  border: 1px solid var(--p-color-stroke-subtle-0);
  border-radius: 8px 8px 0 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SignedInUser = styled.div`
  margin: 8px 0;
  line-height: 19px;
  font-size: 14px;

  > div:first-of-type {
    font-weight: 600;
  }
`;
